import { useOnMount, useOnScreenFocus } from "@/hooks";
import { FIND_CLAIMS } from "@/models/Claim";
import { RouteKeys } from "@/navigationv2";
import { CCGRejectedClaimsRoutes, CCGTabRoutes, ScreenNavigationProps, ScreenOptions } from "@/navigationv2/types";
import { createScreenTitle } from "@/navigationv2/utilities";
import { ClaimStatus } from "libs/types/API";
import React, { useCallback, useEffect, useState } from "react";
import { RefreshControl } from "react-native";
import { useRecoilState, useRecoilValue } from "recoil";

import ClaimProcessing from "../../components/ClaimProcessing";
import ClaimProcessingProvider from "../../components/ClaimProcessing/ClaimProcessingProvider";
import { AppScreen } from "../../layouts/AppScreen";
import { refreshClaimsRequired as recoilRefreshClaimsRequired, userDetails } from "../../utils/recoil";
import { redirectUserAfterLogin } from "../utils";
import { organisationIdFromPropsAndUser } from "./helpers";

interface PropTypes
  extends ScreenNavigationProps<CCGTabRoutes, CCGRejectedClaimsRoutes, RouteKeys.RejectedClaimsScreen> {}

const RejectedClaimsScreen = (props: PropTypes) => {
  const { route } = props;

  const user = useRecoilValue(userDetails);
  const [refreshClaimsRequired, setRefreshClaimsRequired] = useRecoilState(recoilRefreshClaimsRequired);

  const [isUnmounted, setIsUnmounted] = useState(false);
  useEffect(() => () => setIsUnmounted(true), []);
  const onRefresh = useCallback(() => !isUnmounted && setRefreshClaimsRequired(true), [isUnmounted]);
  const organisationId = organisationIdFromPropsAndUser(route, user);
  const selectedQuery = FIND_CLAIMS;

  /**
   * If the user has tried to access a previous url but was unauthenticated,
   * they are dumped to the login screen where `previousUrl` is set. Then on
   * successful log in, this is passed to the screen where a hard redirect
   * is made to the previous route.
   */
  useOnMount(() => {
    redirectUserAfterLogin();
    return () => setIsUnmounted(true);
  });

  useOnScreenFocus(() => {
    setRefreshClaimsRequired(true);
  });

  return (
    <>
      <AppScreen
        contextLabel="Claims"
        pageTitle="Rejected"
        refreshControl={<RefreshControl refreshing={refreshClaimsRequired} onRefresh={onRefresh} />}
        testID="CCG__RejectedClaims__Screen"
      >
        <ClaimProcessingProvider>
          <ClaimProcessing
            orgId={organisationId}
            selectedQuery={selectedQuery}
            claimsScreen={RouteKeys.RejectedClaimsScreen}
            type={ClaimStatus.rejected}
            refreshRequired={refreshClaimsRequired}
            onRefreshComplete={setRefreshClaimsRequired}
          />
        </ClaimProcessingProvider>
      </AppScreen>
    </>
  );
};

const screenOptions: ScreenOptions = {
  title: createScreenTitle("Rejected Claims"),
};

RejectedClaimsScreen.screenOptions = screenOptions;

export default RejectedClaimsScreen;
